import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Toast } from '@/components/ui/toast';
import { supabase } from '@/lib/supabase';
import { CreditCard, AlertCircle, Plus, Trash2, GoalIcon as PaypalIcon, Banknote, DollarSign, Building2, Globe, X } from 'lucide-react';

interface PaymentMethod {
  id: string;
  type: string;
  details: Record<string, string>;
  is_enabled: boolean;
}

interface PaymentSettingsProps {
  sellerId: string;
}

const PAYMENT_TYPES = [
  { id: 'paypal', label: 'PayPal', icon: PaypalIcon, fields: ['email'] },
  { id: 'cashapp', label: 'Cash App', icon: DollarSign, fields: ['cashtag'] },
  { id: 'zelle', label: 'Zelle', icon: Banknote, fields: ['email', 'phone'] },
  { id: 'moncash', label: 'MonCash', icon: DollarSign, fields: ['phone'] },
  { id: 'western_union', label: 'Western Union', icon: Building2, fields: ['full_name'] },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, fields: ['bank_name', 'account_number', 'routing_number'] },
  { id: 'other', label: 'Other', icon: Globe, fields: ['name', 'details'] }
];

export function PaymentSettings({ sellerId }: PaymentSettingsProps) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMethod, setNewMethod] = useState({
    type: '',
    details: {} as Record<string, string>
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, [sellerId]);

  const fetchPaymentMethods = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('seller_id', sellerId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      showError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const addPaymentMethod = async () => {
    try {
      // First, check if this type of payment method already exists
      const existingMethod = paymentMethods.find(method => method.type === newMethod.type);
      if (existingMethod) {
        showError('This payment method type already exists');
        return;
      }

      // Insert the new payment method
      const { data, error } = await supabase
        .from('payment_methods')
        .insert([{
          seller_id: sellerId,
          type: newMethod.type,
          details: newMethod.details,
          is_enabled: true
        }])
        .select()
        .single();

      if (error) throw error;

      // Create notification for admin
      await supabase
        .from('notifications')
        .insert([{
          title: 'Nouvelle méthode de paiement',
          content: `Un vendeur a ajouté une nouvelle méthode de paiement: ${PAYMENT_TYPES.find(t => t.id === newMethod.type)?.label}`,
          type: 'system',
          metadata: {
            seller_id: sellerId,
            payment_method_id: data.id,
            payment_type: newMethod.type
          }
        }]);

      showSuccess('Payment method added successfully');
      setShowAddForm(false);
      setNewMethod({ type: '', details: {} });
      fetchPaymentMethods();
    } catch (err) {
      console.error('Error adding payment method:', err);
      showError('Failed to add payment method');
    }
  };

  const deletePaymentMethod = async (id: string) => {
    try {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showSuccess('Payment method removed successfully');
      fetchPaymentMethods();
    } catch (err) {
      console.error('Error deleting payment method:', err);
      showError('Failed to remove payment method');
    }
  };

  const showSuccess = (message: string) => {
    setToastMessage(message);
    setToastType('success');
    setShowToast(true);
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setToastType('error');
    setShowToast(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2 sm:mb-0">
          Payment Methods
        </h2>
        <Button
          variant="primary"
          onClick={() => setShowAddForm(true)}
          className="flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Payment Method
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Mobile view */}
          <div className="md:hidden space-y-4">
            {paymentMethods.map((method) => {
              const paymentType = PAYMENT_TYPES.find(t => t.id === method.type);
              const IconComponent = paymentType?.icon || Globe;
              
              return (
                <div key={method.id} className="border-2 border-black rounded-lg p-4 hover:border-primary transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center">
                      <IconComponent className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <h3 className="font-medium text-gray-900">{paymentType?.label || method.type}</h3>
                        <div className="mt-1 text-sm text-gray-500">
                          {Object.entries(method.details).map(([key, value]) => (
                            <div key={key} className="capitalize">
                              {key}: {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deletePaymentMethod(method.id)}
                      className="text-red-600 hover:text-red-700 border-2 border-black hover:border-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            
            {paymentMethods.length === 0 && !showAddForm && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-black">
                <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add a payment method to receive payments for your sales
                </p>
              </div>
            )}
          </div>
          
          {/* Desktop view */}
          <div className="hidden md:block">
            {paymentMethods.map((method) => {
              const paymentType = PAYMENT_TYPES.find(t => t.id === method.type);
              const IconComponent = paymentType?.icon || Globe;
              
              return (
                <div key={method.id} className="border-2 border-black rounded-lg p-4 hover:border-primary transition-colors mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <IconComponent className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <h3 className="font-medium text-gray-900">{paymentType?.label || method.type}</h3>
                        <div className="mt-1 text-sm text-gray-500">
                          {Object.entries(method.details).map(([key, value]) => (
                            <div key={key} className="capitalize">
                              {key}: {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deletePaymentMethod(method.id)}
                      className="text-red-600 hover:text-red-700 border-2 border-black hover:border-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            
            {paymentMethods.length === 0 && !showAddForm && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-black">
                <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add a payment method to receive payments for your sales
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Add Payment Method
              </h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Payment Type
                </label>
                <select
                  value={newMethod.type}
                  onChange={(e) => setNewMethod({ type: e.target.value, details: {} })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-2 border-black focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">Select a payment type</option>
                  {PAYMENT_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {newMethod.type && PAYMENT_TYPES.find(t => t.id === newMethod.type)?.fields.map(field => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 capitalize">
                    {field.replace('_', ' ')}
                  </label>
                  <input
                    type="text"
                    value={newMethod.details[field] || ''}
                    onChange={(e) => setNewMethod({
                      ...newMethod,
                      details: { ...newMethod.details, [field]: e.target.value }
                    })}
                    className="mt-1 block w-full border-2 border-black rounded-md shadow-sm focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm px-3 py-2"
                  />
                </div>
              ))}

              <div className="mt-6 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddForm(false)}
                  className="w-full sm:w-auto border-2 border-black hover:border-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={addPaymentMethod}
                  disabled={!newMethod.type || Object.keys(newMethod.details).length === 0}
                  className="w-full sm:w-auto border-2 border-black hover:border-primary"
                >
                  Add Payment Method
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 bg-blue-50 rounded-lg p-4 border-2 border-black">
        <h4 className="text-sm font-medium text-blue-800">
          Important Information:
        </h4>
        <ul className="mt-2 text-sm text-blue-700 space-y-2">
          <li className="flex items-start">
            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            Add multiple payment methods to give buyers more options
          </li>
          <li className="flex items-start">
            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            Keep your payment details up to date
          </li>
          <li className="flex items-start">
            <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            You'll be notified when you receive a payment
          </li>
        </ul>
      </div>
    </div>
  );
}